import { useState } from 'react';
import { toast } from 'react-hot-toast';
import TrackingAPI from 'api/TrackingAPI';
import ChevronIcon from 'assets/button-chevron-left.svg?react';
import { Spinner } from 'components/ira/ui/Spinner';
import { FormikErrors } from 'formik';
import { useCreateCpaPaymentMutation } from 'services/api/cpaApi';
import styled, { css, keyframes } from 'styled-components';
import { getErrorMessage } from 'utils/error';
import { isBlank } from 'utils/storeUtils';

interface FormValues {
  email: string;
}

type PlanType = "monthly" | "yearly";

interface ProcessingState {
  monthly?: boolean;
  yearly?: boolean;
}

interface IActionsProps {
  values: { email: string };
  validateForm: () => Promise<FormikErrors<FormValues>>;
  setFieldTouched: (field: string, touched: boolean, shouldValidate?: boolean) => Promise<void | FormikErrors<FormValues>>;
}

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  height: 24px;
`;

const Actions: React.FC<IActionsProps> = (props) => {
  const { values, validateForm, setFieldTouched } = props;
  const [isProcessing, setIsProcessing] = useState<ProcessingState>({});

  const [createPayment] = useCreateCpaPaymentMutation();

  const handlePaymentClick = async (plan: PlanType) => {
    await setFieldTouched('email', true, false);
    const errors = await validateForm();
    if (Object.keys(errors).length === 0) {
      try {
        setIsProcessing(prev => ({ ...prev, [plan]: true }));
        const amount = plan === "yearly" ? "89.99" : "12.99";
        const response = await createPayment({
          amount,
          email: values.email
        }).unwrap();

        TrackingAPI.trackEvent('cpa_payment_created', { plan: plan });

        toast.dismiss();
        toast.success('Payment created successfully please wait...');
        window.location.href = response.paymentGatewayUrl;
      } catch (error) {
        setIsProcessing({});
        toast.dismiss();
        toast.error(getErrorMessage(error));
      }
    }
  };

  return (
    <PricingPlans>
      <PlanButton
        type="button"
        primary
        onClick={() => handlePaymentClick('yearly')}
        disabled={!isBlank(isProcessing)}
      >
        <ButtonContainer>
          <TextColumn>
            <MainText>Buy for a year</MainText>
            <SubText>$7.49/month</SubText>
          </TextColumn>
          <RightContainer>
            <RightBox>
              <RightText>Save 42%</RightText>
            </RightBox>
            {isProcessing['yearly'] ? (
              <Spinner size={20} />
            ) : (
              <IconContainer>
                <ChevronIcon style={{ height: '100%' }} />
              </IconContainer>
            )}
          </RightContainer>
        </ButtonContainer>
      </PlanButton>

      <PlanButton
        type="button"
        onClick={() => handlePaymentClick('monthly')}
        disabled={!isBlank(isProcessing)}
      >
        <ButtonContainer>
          <TextColumn>
            <MainText>Buy for a month</MainText>
            <SubText>$12.99/month</SubText>
          </TextColumn>
          <RightContainer>
            {isProcessing['monthly'] ? (
              <Spinner size={20} />
            ) : (
              <IconContainer>
                <ChevronIcon style={{ height: '100%' }} />
              </IconContainer>
            )}
          </RightContainer>
        </ButtonContainer>
      </PlanButton>
    </PricingPlans>
  );
};

export default Actions;

const PricingPlans = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 8px;
`;

const RightBox = styled.div`
  height: 24px;
  background-color: white;
  padding: 6px;
  border-radius: 8px;
  display: flex;
  justify-content: center;
`;

const TextColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const RightText = styled.div`
  font-size: 11px;
  font-weight: 700;
  background: linear-gradient(90deg, #9e5eee 0%, #b759ea 32%, #c65de9 66%, #b885f4 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  line-height: 12px;
`;

const ButtonContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0 13px 0 8px;
`;

const SubText = styled.p`
  font-family: Nunito Sans;
  font-weight: 700;
  font-size: 12px;
  line-height: 16px;
`;

const moveAnimation = keyframes`
  0% { left: -200%; }
  100% { left: 200%; }
`;

const PlanButton = styled.button<{ primary?: boolean }>`
  position: relative;
  width: 100%;
  height: 56px;
  padding: 8px 20px 8px 28px;
  border-radius: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  font-family: 'Nunito Sans';
  cursor: pointer;
  color: white;
  overflow: hidden;

  @media (max-width: 375px) {
    height: 48px;
    padding: 8px 16px 8px 20px;
  }

  ${props => props.primary ? css`
    background: linear-gradient(
      90deg,
      rgb(158, 94, 238) 0%,
      rgb(183, 89, 234) 32%,
      rgb(198, 93, 233) 66%,
      rgb(184, 133, 244) 100%
    );
    box-shadow: none;
    border: none;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 10px;
      height: 100%;
      background: rgba(255, 255, 255, 0.5);
      filter: blur(20px);
      animation: ${moveAnimation} 2s ease infinite;
    }
  ` : css`
    background: #282828;
    border: none;
    box-shadow: none;
  `}

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const MainText = styled.p`
  font-family: Nunito Sans;
  font-weight: 700;
  font-size: 18px;
  line-height: 24px;
  letter-spacing: 0%;

  @media (max-width: 399px) {
    font-size: 14px;
  }

  @media (max-width: 375px) {
    font-size: 13px;
    line-height: 18px;
  }
`;

const RightContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-content: center;
  column-gap: 29px;
`;

